import { BehaviorSubject } from 'rxjs';
import { effect, Injectable, signal } from '@angular/core';
import { isNumber, isNotEmpty } from '../utilities';
import { TABLE_VIEW_MODES_ROUTES_MAP } from 'src/app/core/constants/routes-constants';
import { LOCAL_STORAGE_KEYS_TO_KEEP } from '../constants/commonConstants';
import { USER_ROLES } from 'src/app/extensions/constants/commonConstants';

@Injectable()
export class GlobalService {
  obsWorkspace = new BehaviorSubject(this.workspace);
  set workspace(value: app.models.WorkspaceModel) {
    localStorage.setItem('selectedWorkspace', JSON.stringify(value));
    this.obsWorkspace.next(value);
  }
  get workspace() {
    return JSON.parse(localStorage.getItem('selectedWorkspace'));
  }

  set subTenant(value: app.models.WorkspaceModel) {
    localStorage.setItem('subTenant', JSON.stringify(value));
  }
  get subTenant() {
    return JSON.parse(localStorage.getItem('subTenant'));
  }

  set subTenants(value: app.models.WorkspaceModel[]) {
    if (value && Object.keys(value).length != 0) {
      localStorage.setItem('subTenants', JSON.stringify(value));
    }
  }
  get subTenants(): app.models.WorkspaceModel[] {
    return JSON.parse(localStorage.getItem('subTenants')) as app.models.WorkspaceModel[];
  }

  set user(value: app.models.UserProfileModel) {
    localStorage.setItem('user', JSON.stringify(value));
  }

  get user() {
    return JSON.parse(localStorage.getItem('user')) ?? null;
  }

  isAdminUser(): boolean {
    return this.user?.roles.includes(USER_ROLES.TENANT_ADMIN) ?? false;
  }

  set languageTimestamp(value: string) {
    localStorage.setItem('selectedLanguageTimeStamp', value);
  }
  get languageTimestamp() {
    return localStorage.getItem('selectedLanguageTimeStamp');
  }

  set language(value: string) {
    localStorage.setItem('selectedLanguage', value);
    this.languageTimestamp = new Date().getTime().toString();
  }
  get language() {
    return localStorage.getItem('selectedLanguage');
  }

  set appId(value: string) {
    localStorage.setItem('appId', value);
  }

  get appId() {
    return localStorage.getItem('appId');
  }

  private _availableRoutes = null;
  public obsAvailableRoutes = new BehaviorSubject(this._availableRoutes);
  set availableRoutes(values: string[]) {
    if (values && Object.keys(values).length > 0) {
      localStorage.setItem('availableRoutes', JSON.stringify(values));
      this._availableRoutes = values;
      this.obsAvailableRoutes.next(this._availableRoutes);
    }
  }

  get availableRoutes() {
    if ('availableRoutes' in localStorage) {
      return JSON.parse(localStorage.getItem('availableRoutes'));
    }
  }

  set viewModesMap(map: any) {
    if (map && Object.keys(map).length > 0) {
      sessionStorage.setItem('viewModesMap', JSON.stringify(map));
    }
  }

  get viewModesMap() {
    if ('viewModesMap' in sessionStorage) {
      return JSON.parse(sessionStorage.getItem('viewModesMap'));
    }
  }

  initViewModesMap() {
    if (!isNotEmpty(this.viewModesMap)) {
      this.viewModesMap = TABLE_VIEW_MODES_ROUTES_MAP;
    }
  }

  toggleTableViewMode(route: string, tableViewMode: string) {
    if (!this.viewModesMap) {
      this.initViewModesMap();
    }

    let viewModesMap = this.viewModesMap;
    if (route in viewModesMap) {
      viewModesMap[route] = tableViewMode;
      this.viewModesMap = viewModesMap;
    }
  }

  private _numberOfNotification: number = -1;
  public obsNumberOfNotifications = new BehaviorSubject(this._numberOfNotification);
  set numberOfNotifications(value: number) {
    if (!isNumber(value)) return;

    localStorage.setItem('numberOfNotifications', value.toString());
    this._numberOfNotification = value;
    this.obsNumberOfNotifications.next(value);
  }
  get numberOfNotifications() {
    this._numberOfNotification = +localStorage.getItem('numberOfNotifications');
    return this._numberOfNotification;
  }
  private _path: string = '';
  public obsPathChanged = new BehaviorSubject(this._path);
  set pathChanged(value: string) {
    this.obsPathChanged.next(value);
    this._path = value;
    localStorage.setItem('activeMenuPath', value);
  }

  get pathChanged() {
    this._path = localStorage.getItem('activeMenuPath');
    return this._path;
  }

  private _isDrawerExpanded: boolean = true;
  obsIsDrawerExpanded = new BehaviorSubject(this.isDrawerExpanded);
  set isDrawerExpanded(value: boolean) {
    this.obsIsDrawerExpanded.next(value);
    this._isDrawerExpanded = value;
    localStorage.setItem('isDrawerExpanded', JSON.stringify(value));
  }
  get isDrawerExpanded() {
    this._isDrawerExpanded = JSON.parse(localStorage.getItem('isDrawerExpanded'));
    if (this._isDrawerExpanded == null) {
      this._isDrawerExpanded = true;
    }
    return this._isDrawerExpanded;
  }

  public obsNewNotification = new BehaviorSubject(false);

  public hasUserRightsSubject$ = new BehaviorSubject<boolean>(false);
  public hasUserRights$ = this.hasUserRightsSubject$.asObservable();

  public deleteAllLocalStorageExceptKeysToKeep(): void {
    const keysToRemove = Object.keys(localStorage).filter(key => !LOCAL_STORAGE_KEYS_TO_KEEP.includes(key));
    keysToRemove.forEach(key => localStorage.removeItem(key));
  }

  private _signalItem = signal<{ [key: string]: any }>(undefined);
  setSignalItem(signalItemKey: string, data: any) {
    let signalItem = this._signalItem();
    this._signalItem.set({ ...signalItem, ...{ [signalItemKey]: data } });
  }
  getSignalItem(signalItemKey: string) {
    let signalItem = this._signalItem();
    return isNotEmpty(signalItem) ? signalItem[signalItemKey] : undefined;
  }

  // debug _signalItem effect()
  // signalItemEffect = (signalItemKey: string) =>
  //   effect(() => {
  //     let signalItem = this._signalItem();
  //     console.log(isNotEmpty(signalItem) ? signalItem[signalItemKey] : undefined);
  //   });
}
